import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  type TranslationObject,
  type WorkflowAction,
} from '@web-config-app/core';
import { draftErrorFilterKeywords } from '@web-config-app/validation';
import {
  useConfigApp,
  useConfigUserFeedback,
} from '@web-config-app/core-react';
import type { EntityWorkflowActionsProps } from '../../types/hooks';
import { useEntityFormError } from '../use-entity-form-error/use-entity-form-error.hook';

export const useSecondaryAction = ({
  operations,
  status,
  isEntitySaved,
  isReadOnly,
  domainEntity,
}: EntityWorkflowActionsProps): WorkflowAction | undefined => {
  const { formatMessage } = useIntl();
  const { validateEntityData } = useEntityFormError();
  const { frontendValidation: tenantValidation } = useConfigApp();
  const { setErrorFilterKeywordsActionOverride } = useConfigUserFeedback(
    (state) => ({
      setErrorFilterKeywordsActionOverride:
        state.setErrorFilterKeywordsActionOverride,
    }),
  );

  const getActionLabel = (
    defaultLabel: string,
    labelConfig?: TranslationObject,
  ) => {
    if (labelConfig?.text) {
      return labelConfig.text;
    }

    return formatMessage({ id: labelConfig?.key ?? defaultLabel });
  };
  // Conditionally validate draft based on TENANT configuration, then call mutate
  //
  // Behavior:
  // 1) If tenant defines an explicit keyword list for draft validation, use that list.
  // 2) Else if tenant enables draft validation without an explicit list, use our default draftErrorFilterKeywords.
  // 3) Else (not configured), skip validation and just mutate.
  //
  const validateAndMutate = (mutate?: () => void) => {
    const tenantDraftActionValidationKeywords =
      tenantValidation?.draft?.filterOutErrorsByKeywords;
    const draftValidationEnabled = tenantValidation?.draft?.enabled === true;

    if (Array.isArray(tenantDraftActionValidationKeywords)) {
      setErrorFilterKeywordsActionOverride(tenantDraftActionValidationKeywords);
      // Case 1: Use the exact keyword list defined by the tenant
      if (
        validateEntityData({
          filteredErrorKeywords: tenantDraftActionValidationKeywords,
        })
      ) {
        mutate?.();
      }
      return;
    }

    if (draftValidationEnabled) {
      setErrorFilterKeywordsActionOverride(draftErrorFilterKeywords);
      // Case 2: Tenant enabled draft validation but didn't provide a list;
      // fall back to the standard draft keyword set.
      if (
        validateEntityData({
          filteredErrorKeywords: draftErrorFilterKeywords,
        })
      ) {
        mutate?.();
      }
      return;
    }

    // Case 3: Not draft validation configured: proceed without validation
    mutate?.();
  };

  return useMemo(() => {
    switch (status) {
      case 'draft':
        return {
          type: 'update',
          label: getActionLabel(
            'SAVE_CHANGES',
            operations?.update?.actionLabel,
          ),
          disabled: isEntitySaved,
          display: Boolean(operations?.update?.enableAction) && !isReadOnly,
          action: () => validateAndMutate(operations?.update?.mutate),
        };
      case 'published':
        return {
          type: 'update',
          label: getActionLabel(
            'SAVE_CHANGES',
            operations?.update?.actionLabel,
          ),
          display: false,
        };
      case 'archived':
        return {
          type: 'unarchive',
          label: getActionLabel(
            'UN_ARCHIVE',
            operations?.unarchive?.actionLabel,
          ),
          display: Boolean(operations?.unarchive?.enableAction) && !isReadOnly,
          action: operations?.unarchive?.mutate,
        };
      case 'new':
      default:
        return domainEntity.draftingEnabled
          ? {
              type: 'create',
              label: getActionLabel(
                'SAVE_DRAFT',
                operations?.create?.actionLabel,
              ),
              display: Boolean(operations?.create?.enableAction) && !isReadOnly,
              action: () => validateAndMutate(operations?.create?.mutate),
            }
          : undefined;
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    formatMessage,
    isEntitySaved,
    operations?.create?.enableAction,
    operations?.create?.mutate,
    operations?.update?.enableAction,
    operations?.update?.mutate,
    operations?.unarchive?.enableAction,
    operations?.unarchive?.mutate,
    status,
    validateEntityData,
    tenantValidation,
    isReadOnly,
  ]);
};
